import React, { useEffect, useState, useRef } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import ATFM from "./views/ATFM";
import LARA from "./views/LARA";
import icaoData from "../data/ICAO/airports.json";
import atypJsonData from "../data/ICAO/atyp.json";
import { useAuth } from '../context/AuthContext';
import logoutIcon from '../images/logout.svg';
import searchIcon from '../images/search.svg';
import airacIcon from '../images/airac.svg';
import { ChevronDownIcon,  ChevronRightIcon} from '@radix-ui/react-icons';
import { Button } from '@radix-ui/themes';
import pdfIcon from '../images/pdf.png';
import fplGuide from '../data/fpl_guide.pdf';
import asmPdf from '../data/asm.pdf';
import loaEETT from '../data/loa-eett.pdf';
import loaESAA from '../data/loa-esaa.pdf';
import loaENOR from '../data/loa-enor.pdf';
import loaULLL from '../data/loa-ulll.pdf';
import headsetIcon from '../images/headset.svg';
import fullscreenIcon from '../images/fullscreen.svg';
import fullscreenExitIcon from '../images/fullscreenexit.svg';
import { AIS_URL, WIKI_URL, URL_START, orderedAirports } from '../data/config';
import { SWCContent } from "./views/SWC";
import AirportData from "./views/AirportData";
import AirlineData from "./views/AirlineData";
import AircraftType from "./views/AircraftType";
import axios from 'axios';

const TopMenu = ({ setSelectedContent, openWindows, setOpenWindows, setWindowSize }) => {
  const [utcTime, setUtcTime] = useState("00:00:00 UTC");
  const [useFinnishTime, setUseFinnishTime] = useState(false);
  const { user, logout } = useAuth();
  const [airportCharts, setAirportCharts] = useState({});
  const [loadingCharts, setLoadingCharts] = useState({});
  const [laraReservations, setLaraReservations] = useState([]);
  const [atfmFlowMeasures, setAtfmFlowMeasures] = useState([]);
  const [wikiLoading, setWikiLoading] = useState(false);
  const [wikiError, setWikiError] = useState(null);
  const [aipDate, setAipDate] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Update time every second
  useEffect(() => {
    const intervalId = setInterval(() => {
      setUtcTime(formatTime(useFinnishTime));
    }, 1000);
  
    // Also set immediately when useFinnishTime changes
    setUtcTime(formatTime(useFinnishTime));
  
    return () => clearInterval(intervalId);
  }, [useFinnishTime]);

  const formatTime = (useFinnish) => {
    const now = new Date();
  
    if (useFinnish) {
      const finnishTime = now.toLocaleTimeString('fi-FI', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZone: 'Europe/Helsinki',
      });
      return `${finnishTime} SA`;
    } else {
      return `${now.toISOString().slice(11, 19)} UTC`;
    }
  };

  useEffect(() => {
    async function fetchAipDate() {
      try {
        const response = await axios.get(`${URL_START}/api/ais?icao=EFRO`);
        if (response.data.aipDate) {
          setAipDate(response.data.aipDate);
        }
      } catch (error) {
        console.error("Error fetching AIP date:", error);
      }
    }
  
    fetchAipDate();
  }, []);

    const fetchAirportCharts = async (icao) => {
        if (airportCharts[icao]) return; // Already loaded

        setLoadingCharts(prev => ({ ...prev, [icao]: true }));

        try {
            const response = await fetch(AIS_URL + icao);
            const data = await response.json();

            // ✅ Filter out removed charts
            const filteredCharts = (data.charts || []).filter(
                chart => chart.status !== "removed"
            );

            setAirportCharts(prev => ({ ...prev, [icao]: filteredCharts }));
        } catch (error) {
            console.error(`Failed to fetch charts for ${icao}:`, error);
        } finally {
            setLoadingCharts(prev => ({ ...prev, [icao]: false }));
        }
    };
  
  const fetchAndOpenWikiPage = async (contentType, id, windowTitle) => {
      setWikiLoading(true);
      setWikiError(null);
  
      // Create a unique windowId for this request (similar to how you handle the aerodrome window ID in your other code)
      const windowId = `wiki-${contentType}-${id}-${Date.now()}`;
  
      // Open the window initially with the "Loading..." message
      setOpenWindows(prev => [
          ...prev,
          {
              id: windowId,
              title: windowTitle,
              content: <div className="p-2 monospace-font text-sm">Loading...</div>
          }
      ]);
      setWindowSize(3);
  
      try {
          // Fetch the content
          const response = await axios.get(
              `${WIKI_URL}/${contentType}/${id}`,
              { responseType: 'text' }
          );
          
          // Create the WikiContent component with iframe and scroll handling
          const WikiContent = () => {
              const iframeRef = useRef(null);
  
              useEffect(() => {
                  const handleIframeLoad = () => {
                      const iframe = iframeRef.current;
                      if (!iframe) return;
  
                      // Handle anchor clicks within the iframe
                      iframe.contentWindow.document.addEventListener('click', (e) => {
                          const anchor = e.target.closest('a[href^="#"]');
                          if (anchor) {
                              e.preventDefault();
                              const id = anchor.getAttribute('href').substring(1);
                              const element = iframe.contentWindow.document.getElementById(id);
                              if (element) {
                                  element.scrollIntoView({ behavior: 'smooth' });
                              }
                          }
                      });
                  };
  
                  const iframe = iframeRef.current;
                  iframe.addEventListener('load', handleIframeLoad);
  
                  return () => {
                      iframe.removeEventListener('load', handleIframeLoad);
                  };
              }, []);
              
              return (
                <div className="h-full w-full">
                  <iframe 
                      ref={iframeRef}
                      srcDoc={response.data}
                      className="w-full h-full bg-white p-3"
                      style={{
                          transform: "scale(0.9)",
                          transformOrigin: "0 0",
                          width: "111%",
                          height: "111%",
                          border: "none",
                      }}
                      sandbox="allow-same-origin allow-scripts allow-forms allow-top-navigation"
                  />
                </div>
              );
          };
  
          // After data is fetched, update the window with the WikiContent component
          setOpenWindows(prev =>
              prev.map(win =>
                  win.id === windowId
                      ? {
                          ...win,
                          content: <WikiContent />
                      }
                      : win
              )
          );
      } catch (err) {
          console.error('Failed to fetch wiki content:', err);
          setWikiError('Failed to load wiki content');
  
          // If an error occurs, show the error message in the same window
          setOpenWindows(prev =>
              prev.map(win =>
                  win.id === windowId
                      ? {
                          ...win,
                          content: (
                              <div style={{ padding: "20px", color: "red" }}>
                                  Failed to load content: {err.message}
                              </div>
                          )
                      }
                      : win
              )
          );
      } finally {
          setWikiLoading(false);
      }
  };
  

  const openWindow = (title, content, size) => {
    setOpenWindows((prev) => [
      ...prev,
      { id: Date.now(), title, content },
    ]);
    setWindowSize(size);
  };

  // Fetch flow measures when the app starts
  useEffect(() => {
    fetchFlowMeasures();
    const interval = setInterval(fetchFlowMeasures, 300000); // fetch every 5 minutes
    return () => clearInterval(interval); // cleanup the interval on component unmount
  }, []);

  const fetchFlowMeasures = async () => {
    try {
      console.log("Getting flow measures");
      const response = await axios.get("https://ecfmp.vatsim.net/api/v1/flow-measure");
      if (response.data && Array.isArray(response.data)) {
        // notified_flight_information_regions 20 = Helsinki FIR
        const filteredMeasures = response.data.filter((measure) =>
          measure.notified_flight_information_regions?.includes(20)
        );
        setAtfmFlowMeasures(filteredMeasures); // Update state
      }
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  };

  useEffect(() => {
    fetchLaraReservations();
    const interval = setInterval(fetchLaraReservations, 300000);
    return () => clearInterval(interval);
  }, []);

  const fetchLaraReservations = async () => {
    try {
      const response = await axios.get("https://lara-backend.lusep.fi/data/reservations/efin.json");
      if (response.data && Array.isArray(response.data)) {
        setLaraReservations(response.data);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch((err) => {
        console.error(`Error attempting to enable full-screen mode: ${err.message}`);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch((err) => {
        console.error(`Error attempting to exit full-screen mode: ${err.message}`);
      });
    }
  };

  return (
    <>
      <nav className="bg-gray-800 p-0 gap-0 text-gray-300 flex relative z-[100] h-[28px]">

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <Button variant="soft" size="1" className="flex items-center gap-1 text-sm outline-none pl-2 pr-2 hover:bg-gray-900 transition-colors h-full">
              DOCS <ChevronDownIcon className="w-3 h-3" />
            </Button>
          </DropdownMenu.Trigger>
          
          <DropdownMenu.Portal>
            <DropdownMenu.Content className="min-w-[120px] text-white bg-gray-500 p-0 z-[9999] mt-[0px]">
              {/* Nested Menu for SOP */}
              <DropdownMenu.Sub>
                <DropdownMenu.SubTrigger className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none flex justify-between items-center">
                  STANDARD
                  <ChevronRightIcon className="w-3 h-3" />
                </DropdownMenu.SubTrigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.SubContent 
                    className="min-w-[120px] text-white bg-gray-500 p-0 ml-1 z-[9999]"
                    sideOffset={-5}
                    alignOffset={5}
                  >
                    <DropdownMenu.Item 
                      className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none"
                      onClick={() => fetchAndOpenWikiPage('chapter', '20', 'AFIS')}
                    >
                      AFIS
                    </DropdownMenu.Item>
                    <DropdownMenu.Item 
                      className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none"
                      onClick={() => fetchAndOpenWikiPage('chapter', '21', 'AERODROME CONTROL')}
                    >
                      TWR
                    </DropdownMenu.Item>
                    <DropdownMenu.Item 
                      className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none"
                      onClick={() => fetchAndOpenWikiPage('chapter', '23', 'SURVEILLANCE SERVICE (APS, ACS)')}
                    >
                      APS & ACS
                    </DropdownMenu.Item>
                    <DropdownMenu.Item 
                      className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none"
                      onClick={() => fetchAndOpenWikiPage('chapter', '22', 'PROCEDURAL CONTROL')}
                    >
                      APP
                    </DropdownMenu.Item>
                    <DropdownMenu.Separator className="h-px bg-gray-200" />
                    <DropdownMenu.Item 
                      className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none"
                      onClick={() => fetchAndOpenWikiPage('page', '167', 'TOWER VFR FLIGHTS')}
                    >
                      VFR IN CTR
                    </DropdownMenu.Item>
                    <DropdownMenu.Item 
                      className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none"
                      onClick={() => fetchAndOpenWikiPage('page', '214', 'VFR IN TERMINAL AREA')}
                    >
                      VFR IN TMA
                    </DropdownMenu.Item>
                  </DropdownMenu.SubContent>
                </DropdownMenu.Portal>
              </DropdownMenu.Sub>

              {/* Nested Menu for LOP */}
              <DropdownMenu.Sub>
                <DropdownMenu.SubTrigger className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none flex justify-between items-center">
                  LOCAL
                  <ChevronRightIcon className="w-3 h-3" />
                </DropdownMenu.SubTrigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.SubContent 
                    className="min-w-[120px] text-white bg-gray-500 p-0 ml-1 z-[9999]"
                    sideOffset={-5}
                    alignOffset={5}
                  >
                    <DropdownMenu.Item 
                      className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none"
                      onClick={() => fetchAndOpenWikiPage('chapter', '25', 'HK TWR TOIMINTAKÄSIKIRJA')}
                    >
                      HK TWR
                    </DropdownMenu.Item>
                    <DropdownMenu.Item 
                      className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none"
                      onClick={() => fetchAndOpenWikiPage('chapter', '26', 'HK APP TOIMINTAKÄSIKIRJA')}
                    >
                      HK APP
                    </DropdownMenu.Item>
                    <DropdownMenu.Item 
                      className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none"
                      onClick={() => fetchAndOpenWikiPage('chapter', '27', 'EFIN ACC TOIMINTAKÄSIKIRJA')}
                    >
                      EFIN ACC
                    </DropdownMenu.Item>
                    <DropdownMenu.Item 
                      className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none"
                      onClick={() => fetchAndOpenWikiPage('chapter', '28', 'VERKOSTOKENTÄT TOIMINTAKÄSIKIRJA')}
                    >
                      REGIONAL
                    </DropdownMenu.Item>
                    <DropdownMenu.Separator className="h-px bg-gray-200" />
                    <DropdownMenu.Item 
                      className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none inline-flex items-center gap-1 w-1/2"
                      onClick={() => fetchAndOpenWikiPage('page', '210', 'VFR FLIGHT FROM A TO B')}
                    >
                      <img className="w-3" src={headsetIcon} alt="PDF"/> VFR
                    </DropdownMenu.Item>
                    <DropdownMenu.Item 
                      className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none inline-flex items-center gap-1 w-1/2"
                      onClick={() => fetchAndOpenWikiPage('page', '169', 'IFR FLIGHT FROM A TO B')}
                    >
                      <img className="w-3" src={headsetIcon} alt="PDF"/> IFR
                    </DropdownMenu.Item>
                  </DropdownMenu.SubContent>
                </DropdownMenu.Portal>
              </DropdownMenu.Sub>

              {/* Nested Menu for LOA */}
              <DropdownMenu.Sub>
                <DropdownMenu.SubTrigger className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none flex justify-between items-center">
                  AGREEMENTS
                  <ChevronRightIcon className="w-3 h-3" />
                </DropdownMenu.SubTrigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.SubContent 
                    className="min-w-[120px] text-white bg-gray-500 p-0 ml-1 z-[9999]"
                    sideOffset={-5}
                    alignOffset={5}
                  >
                    <DropdownMenu.Item className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none inline-flex items-center gap-2 w-full"
                      onClick={() =>
                        openWindow("EFIN - EETT LOA",
                          <iframe style={{ width: "100%", height: "100%" }} src={loaEETT}/>, 3
                        )
                      }
                    >
                      <img className="w-3" src={pdfIcon} alt="PDF"/> EETT FIR
                    </DropdownMenu.Item>
                    <br />
                    <DropdownMenu.Item className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none inline-flex items-center gap-2 w-full"
                      onClick={() =>
                        openWindow("EFIN - ENOR LOA",
                          <iframe style={{ width: "100%", height: "100%" }} src={loaENOR}/>, 3
                        )
                      }
                    >
                      <img className="w-3" src={pdfIcon} alt="PDF"/> ENOR FIR
                    </DropdownMenu.Item>
                    <br />
                    <DropdownMenu.Item className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none inline-flex items-center gap-2 w-full"
                      onClick={() =>
                        openWindow("EFIN - ULLL LOA",
                          <iframe style={{ width: "100%", height: "100%" }} src={loaULLL}/>, 3
                        )
                      }
                    >
                      <img className="w-3" src={pdfIcon} alt="PDF"/> ULLL FIR
                    </DropdownMenu.Item>
                    <br />
                    <DropdownMenu.Item className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none inline-flex items-center gap-2 w-full"
                      onClick={() =>
                        openWindow("EFIN - ESAA LOA",
                          <iframe style={{ width: "100%", height: "100%" }} src={loaESAA}/>, 3
                        )
                      }
                    >
                      <img className="w-3" src={pdfIcon} alt="PDF"/> ESAA FIR
                    </DropdownMenu.Item>

                  </DropdownMenu.SubContent>
                </DropdownMenu.Portal>
              </DropdownMenu.Sub>
      
              <DropdownMenu.Separator className="h-px bg-gray-200" />
              <DropdownMenu.Item 
                className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none"
                onClick={() => fetchAndOpenWikiPage('chapter', '31', 'MEDICAL HELICOPTER FLIGHTS')}
              >
                HEMS
              </DropdownMenu.Item>
              <DropdownMenu.Item 
                className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none"
                onClick={() => fetchAndOpenWikiPage('chapter', '32', 'MILITARY PROCEDURES')}
              >
                MIL
              </DropdownMenu.Item>
              <DropdownMenu.Separator className="h-px bg-gray-200" />
              <DropdownMenu.Sub>
                <DropdownMenu.SubTrigger 
                  className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none flex justify-between items-center"
                  onMouseEnter={() => {
                    // Preload data for all airports when hovering the parent item
                    orderedAirports.forEach(airport => {
                      if (!airportCharts[airport.icao]) {
                        fetchAirportCharts(airport.icao);
                      }
                    });
                  }}
                >
                  AIP AD 2 CHARTS
                  <ChevronRightIcon className="w-3 h-3" />
                </DropdownMenu.SubTrigger>
                
                <DropdownMenu.Portal>
                  <DropdownMenu.SubContent className="min-w-[120px] max-h-[100vh] overflow-y-auto text-white bg-gray-500 p-0 ml-1 z-[9999]" sideOffset={-5} alignOffset={5}>
                    {orderedAirports.map(airport => (
                      <DropdownMenu.Sub key={airport.icao}>
                        <DropdownMenu.SubTrigger 
                          className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none flex justify-between items-center"
                          onMouseEnter={() => fetchAirportCharts(airport.icao)}
                        >
                          {airport.icao}
                          <ChevronRightIcon className="w-3 h-3" />
                        </DropdownMenu.SubTrigger>
                        
                        <DropdownMenu.Portal>
                          <DropdownMenu.SubContent 
                            className="min-w-[200px] max-h-[80vh] text-white bg-gray-500 p-0 ml-1 overflow-y-auto z-[9999]"
                            sideOffset={-5}
                            alignOffset={5}
                          >
                              <div className="sticky top-0 bg-gray-600 px-3 py-2 border-b border-gray-500">
                                <div className="text-sm text-gray-300">{airport.icao} {orderedAirports.find(a => a.icao === airport.icao)?.name}</div>
                              </div>
                            {loadingCharts[airport.icao] ? (
                              <div className="text-sm px-2 py-1 text-gray-400">Loading...</div>
                            ) : (
                              airportCharts[airport.icao]?.map((chart) => (
                                <>
                                  <DropdownMenu.Item 
                                    key={chart.link}
                                    className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none inline-flex items-center gap-2 w-full"
                                    onClick={() =>
                                      openWindow(
                                        `${airport.icao} - ${chart.chart}`,
                                        <iframe 
                                          style={{ width: "100%", height: "100%" }} 
                                          src={chart.link}
                                          title={`${airport.icao} - ${chart.chart}`}
                                        />, 3
                                      )
                                    }
                                  >
                                    <img className="w-3" src={pdfIcon} alt="PDF" /> 
                                    {chart.chart}
                                  </DropdownMenu.Item>
                                  <br />
                                </>
                              ))
                            )}
                          </DropdownMenu.SubContent>
                        </DropdownMenu.Portal>
                      </DropdownMenu.Sub>
                    ))}
                  </DropdownMenu.SubContent>
                </DropdownMenu.Portal>
              </DropdownMenu.Sub>

              <DropdownMenu.Separator className="h-px bg-gray-200" />
              <DropdownMenu.Item className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none inline-flex items-center gap-2"
                onClick={() =>
                  openWindow("GEN T1-10",
                    <iframe style={{ width: "100%", height: "100%" }} src="https://www.traficom.fi/sites/default/files/media/regulation/GEN%20T1-10%20revisio%205.pdf"/>,
                    3
                  )
                }
              >
                <img className="w-3" src={pdfIcon} alt="PDF"/> T1-10
              </DropdownMenu.Item>
              <DropdownMenu.Item className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none inline-flex items-center gap-2"
                onClick={() =>
                  openWindow("FPL TÄYTTÖOHJEET",
                    <iframe style={{ width: "100%", height: "100%" }} src={fplGuide}/>,
                    3
                  )
                }
              >
                <img className="w-3" src={pdfIcon} alt="PDF"/> FPL
              </DropdownMenu.Item>
              <DropdownMenu.Item className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none inline-flex items-center gap-2"
                onClick={() =>
                  openWindow("ASM TOIMINTAKÄSIKIRJA",
                    <iframe style={{ width: "100%", height: "100%" }} src={asmPdf}/>,
                    3
                  )
                }
              >
                <img className="w-3" src={pdfIcon} alt="PDF"/> ASM
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <Button variant="soft" size="1" className="flex items-center gap-1 text-sm outline-none pl-2 pr-2 hover:bg-gray-900 transition-colors">
              MET <ChevronDownIcon className="w-3 h-3" />
            </Button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content className="min-w-[120px] text-white bg-gray-500 p-0 z-[1000] mt-[0px]">
              <DropdownMenu.Item className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none"
                onClick={() => window.open('https://wx.lusep.fi/', '_blank', 'width=800,height=600')}>
                AVIMET
              </DropdownMenu.Item>
              <DropdownMenu.Item className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none"
                onClick={() => window.open('https://wx.lusep.fi/awosview/eftp.html?theme=dark', '_blank', 'width=800,height=600')}>
                AWOSVIEW
              </DropdownMenu.Item>
              <DropdownMenu.Item className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none"
                onClick={() => openWindow("SWC", <SWCContent />,3) }>
                SWC
              </DropdownMenu.Item>
              <DropdownMenu.Item className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none"
                onClick={() =>
                  openWindow("ILMAILUSAA",
                    <iframe style={{ width: "100%", height: "100%" }} src="https://ilmailusaa.fi/"/>,
                    3
                  )
                }>
                ILMAILUSÄÄ
              </DropdownMenu.Item>

            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>

        <Button
          className="flex items-center gap-1 text-sm outline-none pl-2 pr-2 hover:bg-gray-900 transition-colors"
          onClick={() =>
            openWindow(
              "	AIR TRAFFIC FLOW MANAGEMENT",
              <ATFM flowMeasures={atfmFlowMeasures} onFlowMeasuresChange={setAtfmFlowMeasures} />,
              3
            )
          }
        >
          ATFM <span className={`w-5 ${atfmFlowMeasures.length > 0 ? 'bg-yellow-400 text-black' : 'bg-gray-700 text-gray-400'}`}>
            {atfmFlowMeasures.length}
          </span>
        </Button>

        <Button
          className="flex items-center gap-1 text-sm outline-none pl-2 pr-2 hover:bg-gray-900 transition-colors"
          onClick={() =>
            openWindow(
              "LARA RESERVATIONS",
              <LARA reservations={laraReservations} onReservationsChange={setLaraReservations} />,
              2
            )
          }
        >
          LARA <span className={`w-5 ${laraReservations.length > 0 ? 'bg-yellow-400 text-black' : 'bg-gray-700 text-gray-400'}`}>
            {laraReservations.length}
          </span>
        </Button>

        <Button
          className="flex items-center gap-1 text-sm outline-none pl-2 pr-2 hover:bg-gray-900 transition-colors"
          onClick={() =>
            openWindow(
              "FLYK MAP",
              <iframe
                style={{ width: "100%", height: "100%" }}
                src="https://flyk.com/map"
              />,
              3
            )
          }
        >
          FLYK
        </Button>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <Button variant="soft" size="1" className="flex items-center gap-1 text-sm outline-none pl-2 pr-2 hover:bg-gray-900 transition-colors">
              INFO <img className="w-[15px]" src={searchIcon} alt="Search" /> <ChevronDownIcon className="w-3 h-3" />
            </Button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content className="min-w-[120px] text-white bg-gray-500 p-0 z-[1000] mt-[0px]">
              <DropdownMenu.Item className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none"
                onClick={() => openWindow(
                  "ACC SECTORS",
                  <iframe
                    style={{
                      width: "100%",
                      height: "100%",
                    }}
                    src="https://sectors.lusep.fi/"
                  />,
                  3
                )}>
                ACC SECTORS
              </DropdownMenu.Item>
              <DropdownMenu.Item
                className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none flex items-center"
                onClick={() => openWindow("AERODROME DATA", <AirportData />, 3)}
              >
                <img className="w-[15px] mr-2" src={searchIcon} alt="Search" />
                AERODROME
              </DropdownMenu.Item>
              <DropdownMenu.Item
                className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none flex items-center"
                onClick={() => openWindow("AIRCRAFT TYPE DATA", <AircraftType />, 2)}
              >
                <img className="w-[15px] mr-2" src={searchIcon} alt="Search" />
                AIRCRAFT TYPE
              </DropdownMenu.Item>
              <DropdownMenu.Item
                className="text-sm px-2 py-1 hover:bg-gray-700 cursor-pointer outline-none flex items-center"
                onClick={() => openWindow("AIRLINE DATA", <AirlineData />, 3)}
              >
                <img className="w-[15px] mr-2" src={searchIcon} alt="Search" />
                AIRLINE
              </DropdownMenu.Item>
              <DropdownMenu.Separator className="h-px bg-gray-200" />
              <DropdownMenu.Item
                className="text-xs px-2 py-1 hover:bg-gray-700 text-gray-300 cursor-pointer outline-none flex items-center"
                onClick={() => fetchAndOpenWikiPage('page', '128', 'OPERATIONAL CHANGES')}
              >
                <img className="w-[15px] mr-2" src={airacIcon} alt="AIRAC" />
                {aipDate && `${aipDate}`}
              </DropdownMenu.Item>


            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>

        {/* Right side of top menu */}

        <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0">

          <button onClick={() => setUseFinnishTime(prev => !prev)} className="text-xs p-1 monospace-font max-[570px]:hidden hover:bg-gray-900 transition-colors">
            {utcTime}
          </button>

          <button onClick={toggleFullscreen} className="p-1 hover:bg-gray-900 transition-colors">
            <img className="w-[19px]" src={isFullscreen ? fullscreenExitIcon : fullscreenIcon} alt={"View"} />
          </button>

          <button onClick={logout} className="p-1 hover:bg-gray-900 transition-colors">
            <img className="w-[19px]" src={logoutIcon} alt="Logout" />
          </button>
        </div>

      </nav>
    </>
  );
};

export default TopMenu;
